import React from "react";
import { User, EnableUser, DisableUser, PinRegenerate } from "../../models/userModels";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import { Box, MenuItem } from "@mui/material";
import TableActionButton from "../../../../shared/components/tables/TableActionButton";
import PinOutlinedIcon from "@mui/icons-material/PinOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import StopScreenShareOutlinedIcon from "@mui/icons-material/StopScreenShareOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import { menuItemStyles } from "../../styles/employees";
import { getButtonStates } from "../../utils/employeeUtils";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";

interface TemporaryAccessActionsProps {
  user: User;
  onPinRegenerate: (data: PinRegenerate) => void;
  onEnableTemporaryAccess: (model: EnableUser) => void;
  onDisableTemporaryAccess: (model: DisableUser) => void;
  onDeleteUser: (id: string) => void;
  translate: (key: string, params?: any) => string;
  translateCommon: (key: string, params?: any) => string;
}
const TemporaryAccessActions = ({
  user,
  onPinRegenerate,
  onDisableTemporaryAccess,
  onEnableTemporaryAccess,
  onDeleteUser,
  translate,
  translateCommon
}: TemporaryAccessActionsProps) => {
  const confirm = useConfirmation();
  const buttonStates = getButtonStates(user);

  return (
    <Box className="flex justify-end w-full">
      <TableActionButton stopPropagation={true}>
        {buttonStates.regeneratePin && (
          <MenuItem
            sx={menuItemStyles}
            onClick={() => onPinRegenerate({
              userId: user.id,
              visitPurpose: user.visitPurpose ?? ""
            })}
            disabled={buttonStates.regeneratePinDisabled}
          >
            <PinOutlinedIcon />
            {translate("regeneratePIN")}
          </MenuItem>
        )}

        {buttonStates.disable && (
          <MenuItem
            sx={menuItemStyles}
            onClick={() => onDisableTemporaryAccess({ userId: user.id })}
          >
            <StopScreenShareOutlinedIcon />
            {translate("disable")}
          </MenuItem>
        )}

        {buttonStates.activate && (
          <MenuItem
            sx={menuItemStyles}
            onClick={() => onEnableTemporaryAccess({ userId: user.id })}
          >
            <ComputerOutlinedIcon />
            {translate("activate")}
          </MenuItem>
        )}

        {buttonStates.delete && (
          <PermissionGuard module={Modules.Users} level={PermissionLevel.Admin}>
            <MenuItem
              sx={menuItemStyles}
              onClick={() =>
                confirm({
                  title: translate("deleteUserTitle"),
                  text: translate("deleteUserText", { userName: user.userName }),
                  confirmButton: translate("deleteUserConfirm"),
                  closeButton: translate("deleteUserCancel"),
                  severity: "error",
                  onConfirm: () => onDeleteUser(user.id)
                })
              }
            >
              <DeleteOutlinedIcon color="inherit" />
              {translateCommon("buttons.delete")}
            </MenuItem>
          </PermissionGuard>
        )}
      </TableActionButton>
    </Box>
  );
};

export default TemporaryAccessActions;